/* ===== URL Sanitization for Image Sources ===== */
/* Prevents XSS via malicious URLs in img src attributes */

/**
 * Sanitizes image source URL for safe use in img src attributes
 * @param {string} input - URL to sanitize
 * @returns {string|null} - Sanitized URL or null if invalid/unsafe
 */
/** Ghilimelele / < > nu trebuie să poată ieși din atributul src="…" */
function attrSafe(u) {
  return String(u).replace(/"/g, '%22').replace(/'/g, '%27').replace(/</g, '%3C').replace(/>/g, '%3E');
}

export function sanitizeImageSrc(input) {
  if (!input || typeof input !== 'string') return null;
  
  const trimmed = input.trim();
  if (!trimmed) return null;
  
  // Block javascript: URLs (XSS risk)
  if (trimmed.toLowerCase().startsWith('javascript:')) {
    return null;
  }
  
  // Block data:text/html URLs (XSS risk)
  if (trimmed.toLowerCase().startsWith('data:text/html')) {
    return null;
  }
  
  // Allow data:image/* URLs (for FileReader previews - safe)
  if (trimmed.toLowerCase().startsWith('data:image/')) {
    // Validate data URL format: data:image/[type];base64,[data]
    const dataUrlMatch = trimmed.match(/^data:image\/([a-z]+);base64,/i);
    if (dataUrlMatch) {
      return attrSafe(trimmed); // Valid base64 image data URL
    }
    // Also allow data:image/[type],[data] (non-base64, less common but valid)
    if (trimmed.match(/^data:image\/[a-z]+,/i)) {
      return attrSafe(trimmed);
    }
    return null; // Invalid data URL format
  }
  
  // Allow blob: URLs (for FileReader/blob previews)
  if (trimmed.toLowerCase().startsWith('blob:')) {
    return attrSafe(trimmed);
  }
  
  // Allow relative URLs starting with / (but not //host — that would bypass the https allow-list)
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return attrSafe(trimmed);
  }
  
  // Allow https:// URLs only (not http:// for security)
  if (trimmed.toLowerCase().startsWith('https://')) {
    try {
      const url = new URL(trimmed);
      const hostname = url.hostname.toLowerCase();
      
      // Whitelist allowed hosts
      const allowedHosts = [
        'firebasestorage.googleapis.com',
        'firebasestorage.app',
        'storage.googleapis.com'
      ];
      
      // Check if hostname matches allowed hosts or subdomains
      const isAllowed = allowedHosts.some(allowed => {
        return hostname === allowed || hostname.endsWith('.' + allowed);
      });
      
      if (isAllowed) {
        return attrSafe(trimmed);
      }
      
      // For other hosts, block (can be extended later if needed)
      return null;
    } catch (e) {
      // Invalid URL format
      return null;
    }
  }
  
  // Block everything else (http://, ftp://, etc.)
  return null;
}

/**
 * Safe http(s) URL for opening in a new tab (user-provided links: Facebook, Google review, etc.).
 * @param {string} input
 * @returns {string|null}
 */
export function sanitizeExternalHttpUrl(input) {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (lower.startsWith('javascript:') || lower.startsWith('data:')) return null;
  try {
    const u = new URL(trimmed);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return trimmed;
  } catch {
    return null;
  }
}
